import Nango from '@nangohq/frontend';
import useRetrieveIntegrationSessionToken from '@/hooks/api/integrations/useRetrieveIntegrationSessionToken.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { INTEGRATIONS_KEY } from '@/utils/queryKeys.ts';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';

import { IntegrationProvider } from '@/types/integrations.ts';

const invalidateIntegrationQueries = (invalidateQueries: (delayMs: number) => void) => {
  invalidateQueries(2000);
  invalidateQueries(4000);
  invalidateQueries(8000);
};

const handleIntegrationCreated = (invalidateQueries: (delayMs: number) => void) => {
  invalidateIntegrationQueries(invalidateQueries);
  notifications.show({
    title: 'Success',
    message: 'Successfully enabled integration',
    color: 'blue',
  });
};

const useCreateNangoIntegration = () => {
  const { mutateAsync: AddIntegration, isPending } = useRetrieveIntegrationSessionToken();
  const queryClient = useQueryClient();

  const createIntegration = useCallback(
    async (provider: IntegrationProvider, sharepointTenantId?: string) => {
      const normalizedSharepointTenantId = sharepointTenantId?.trim() || undefined;
      const response = await AddIntegration({
        integration_type: provider.name,
        sharepoint_tenant_id: normalizedSharepointTenantId,
      });
      const nango = new Nango({ connectSessionToken: response.token });

      nango.openConnectUI({
        onEvent: (event) => {
          if (event.type === 'close') {
            // Handle modal closed.
          } else if (event.type === 'connect') {
            // This polls a few times because the integration record is created asynchronously.
            handleIntegrationCreated((delayMs) => {
              setTimeout(
                () => queryClient.invalidateQueries({ queryKey: INTEGRATIONS_KEY }),
                delayMs,
              );
            });
          }
        },
      });
    },
    [AddIntegration, queryClient],
  );

  const createIntegrationFromButton = useCallback(
    async (provider: IntegrationProvider) => {
      try {
        await createIntegration(provider);
      } catch (error) {
        // These failures are already displayed by ApiClientProvider. Modal
        // callers use createIntegration directly so they can stay open.
        const response = isAxiosError<{ code?: string }>(error) ? error.response : undefined;
        const expectedFailure =
          (response?.status === 503 &&
            response.data?.code === 'integration_connection_capacity_reached') ||
          (response?.status === 504 && response.data?.code === 'integration_connection_timeout');
        if (!expectedFailure) {
          throw error;
        }
      }
    },
    [createIntegration],
  );

  return {
    createIntegration,
    createIntegrationFromButton,
    creatingIntegration: isPending,
  };
};

export default useCreateNangoIntegration;
